import { Component, signal, computed, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AuthService } from '../../../features/auth/auth.service';
import { BackofficeAccessService } from './sidebar.service';
import { Navbar } from '../navbar/navbar';

interface SidebarItem {
  label: string;
  icon?: string;
  route?: string;
  badge?: string;
  moduleKey?: string;
  parentModuleKey?: string;
  children?: SidebarItem[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatSidenavModule, MatButtonModule, Navbar],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss'],
})
export class Sidebar implements OnInit, AfterViewInit {
  @ViewChild('drawer', { static: true }) sidenav!: MatSidenav; // usa el template reference explícito
  isMobile = signal(false);
  activeGroups = signal<Record<string, boolean>>({});
  userFullName = signal<string | null>(localStorage.getItem('user_full_name'));
  roles = signal<string[]>(
    (() => {
      const raw = localStorage.getItem('roles');
      try {
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    })(),
  );

  constructor(
    private authService: AuthService,
    private router: Router,
    private backofficeAccessService: BackofficeAccessService,
    breakpoint: BreakpointObserver,
  ) {
    breakpoint.observe(['(max-width: 959px)']).subscribe((result) => {
      const mobile = result.matches;
      this.isMobile.set(mobile);

      // avoid layout jump: only act if sidenav already initialized
      if (this.sidenav) {
        if (mobile) {
          this.sidenav.close();
        } else {
          this.sidenav.open();
        }
      }
    });
    window.addEventListener('storage', () => {
      this.userFullName.set(localStorage.getItem('user_full_name'));
      const raw = localStorage.getItem('roles');
      try {
        this.roles.set(raw ? JSON.parse(raw) : []);
      } catch {
        this.roles.set([]);
      }
    });
  }

  ngOnInit() {
    // initialize sidenav BEFORE first paint to avoid layout shift
    if (this.isMobile()) {
      this.sidenav.close();
    } else {
      this.sidenav.open();
    }

  }

  menu: SidebarItem[] = [
    {
      label: 'Operación',
      children: [
        { label: 'Dashboard', icon: 'dashboard', route: '/', moduleKey: 'dashboard' },
        { label: 'Clientes', icon: 'groups', route: '/clientes', badge: '6', moduleKey: 'clients' },
        {
          label: 'Conexiones HPIH',
          icon: 'hub',
          route: '/Conexiones-HPIH',
          moduleKey: 'clients',
        },
        {
          label: 'Oposiciones',
          icon: 'block',
          route: '/oposiciones',
          badge: '14',
          moduleKey: 'oppositions',
        },
      ],
    },
    {
      label: 'Análisis',
      children: [
        { label: 'Métricas y uso', icon: 'bar_chart', route: '/metricas', moduleKey: 'metrics_and_usage' },
        { label: 'Auditoría', icon: 'task', route: '/auditoria', moduleKey: 'audit' },
      ],
    },
    {
      label: 'Administración',
      children: [
        { label: 'Usuarios y roles', icon: 'group_add', route: '/usuarios', moduleKey: 'users_and_roles' },
        { label: 'Credenciales de API', icon: 'key', route: '/credenciales', moduleKey: 'api_credentials' },
      ],
    },
    {
      label: 'Cerrar sesión',
      icon: 'logout',
    },
  ];

  toggleGroup(label: string) {
    this.activeGroups.update((v) => ({ ...v, [label]: !v[label] }));
  }

  isOpen(label: string) {
    return !!this.activeGroups()[label];
  }

  toggleSidebar() {
    if (!this.sidenav) return;
    this.sidenav.toggle();
  }

  goHome() {
    this.router.navigate(['/']);
  }

  ngAfterViewInit() {
    // no-op: handled earlier to avoid flicker
  }

  navigate(item: SidebarItem) {
    if (this.isDisabled(item)) {
      return;
    }

    if (item.label === 'Cerrar sesión') {
      this.logout();
      return;
    }

    if (item.route) {
      this.router.navigate([item.route]);
      if (this.isMobile()) this.sidenav.close();
    }
  }

  isActive(item: SidebarItem): boolean {
    if (!item.route) return false;

    if (item.route === '/') {
      return this.router.url === '/';
    }

    return this.router.url.startsWith(item.route);
  }

  isVisible(item: SidebarItem): boolean {
    // items públicos/sin permisos.
    if (!item.moduleKey) {
      return true;
    }

    // si backend ya envía el módulo específico,
    // usamos exclusivamente ese valor.
    if (this.backofficeAccessService.hasModuleKey(item.moduleKey)) {
      return this.backofficeAccessService.hasModule(item.moduleKey);
    }

    // fallback temporal para módulos legacy/macros.
    if (item.parentModuleKey) {
      return this.backofficeAccessService.hasModule(item.parentModuleKey);
    }

    // si no existe en access => ocultar.
    return false;
  }

  isDisabled(item: SidebarItem): boolean {
    if (!item.moduleKey) {
      return false;
    }

    // si existe explícitamente y está en false.
    if (this.backofficeAccessService.hasModuleKey(item.moduleKey)) {
      return !this.backofficeAccessService.hasModule(item.moduleKey);
    }

    // si depende de un parent legacy.
    if (item.parentModuleKey) {
      return !this.backofficeAccessService.hasModule(item.parentModuleKey);
    }

    // no existe en access.
    return true;
  }

  hasVisibleChildren(item: SidebarItem): boolean {
    if (!item.children?.length) return false;

    return item.children.some((child) => this.isVisible(child));
  }

  logout() {
    this.authService.logout().subscribe({
      complete: () => this.router.navigate(['/login']),
      error: () => this.router.navigate(['/login']),
    });
  }

  roleLabel = computed(() => {
    const role = this.roles()[0];
    return role ? role.toUpperCase() : null;
  });
}
